import { getApiData, postFormData } from "../utils/APIHelper";

const BUDGET_ENDPOINTS = {
    GET_ROOMS_BOQS: (projectId) => `api/project/${projectId}/rooms/boqs`,
    GET_INCLUSIONS_BOQS: (projectId) => `api/project/${projectId}/inclusions/boqs`,
    GET_AVAILABLE_INCLUSIONS: (projectType) => `api/rooms/inclusions?project_type=${projectType}`,
    GET_AVAILABLE_INCLUSIONS_FOR_ROOM: (projectType, roomName) =>
        `api/rooms/inclusions?project_type=${encodeURIComponent(projectType)}&room_name=${encodeURIComponent(roomName)}`,
    GET_ROOM_INCLUSIONS: (projectId, roomId) => `api/project/${projectId}/room/${roomId}/inclusions`,
    GET_SUBCATEGORIES: (category, projectType, roomName) => {
        const base = `api/rooms/inclusions/subcategories?category=${encodeURIComponent(category)}&project_type=${encodeURIComponent(projectType || "")}`;
        return roomName ? `${base}&room_name=${encodeURIComponent(roomName)}` : base;
    },
    ADD_INCLUSION_TO_ROOM: (projectId, roomId) => `api/project/${projectId}/room/${roomId}/inclusion`,
    ADD_BOQ_TO_INCLUSION: (projectId, inclusionId) => `api/project/${projectId}/inclusion/${inclusionId}/boq`,
    DELETE_BOQ: (projectId, boqId) => `api/project/${projectId}/boq/${boqId}`,
    UPDATE_BOQ: (projectId, boqId) => `api/project/${projectId}/boq/${boqId}`,
    DELETE_INCLUSION: (inclusionId) => `api/project/inclusion/${inclusionId}`,
    GET_ROOM_ANALYTICS: (projectId) => `api/project/${projectId}/rooms/analytics`,
    GET_BUDGET_ANALYTICS: (projectId, grouping) =>
        grouping ? `api/project/${projectId}/boqs/analytics?grouping=${grouping}` : `api/project/${projectId}/boqs/analytics`,
    CREATE_ROOMS_CONFIGURATION: (projectId) => `api/project/${projectId}/configuration/rooms`,
    GET_ROOM_ACTIVITIES: (roomId) => `api/room/${roomId}/activities`,
    DELETE_FILE: (fileId) => `api/project/delete-files/${fileId}`,
    UPLOAD_FILE: `api/project/files`,
};

export const getRoomsBOQs = async (projectId) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.GET_ROOMS_BOQS(projectId),
            "GET",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error fetching rooms BOQs:", error);
        throw error;
    }
};

export const getInclusionsBOQs = async (projectId) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.GET_INCLUSIONS_BOQS(projectId),
            "GET",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error fetching inclusions BOQs:", error);
        throw error;
    }
};

export const getAvailableInclusions = async (projectType) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.GET_AVAILABLE_INCLUSIONS(projectType),
            "GET",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error fetching available inclusions:", error);
        throw error;
    }
};

export const getRoomAnalytics = async (projectId) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.GET_ROOM_ANALYTICS(projectId),
            "GET",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error fetching room analytics:", error);
        throw error;
    }
};

export const getBudgetAnalytics = async (projectId, grouping = null) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.GET_BUDGET_ANALYTICS(projectId, grouping),
            "GET",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error fetching budget analytics:", error);
        throw error;
    }
};

export const addInclusionToRoom = async (projectId, roomId, inclusionName) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.ADD_INCLUSION_TO_ROOM(projectId, roomId),
            "POST",
            { name: inclusionName }
        );
        return response;
    } catch (error) {
        console.error("Error adding inclusion to room:", error);
        throw error;
    }
};

export const addBOQToInclusion = async (projectId, inclusionId, itemData) => {
    try {
        const formData = new FormData();
        formData.append('name', itemData.item_name);
        formData.append('type', itemData.boq_type);
        formData.append('subcategory', itemData.subcategory?.label || itemData.subcategory);
        formData.append('supply_type', itemData.supply_apply?.label || itemData.supply_apply);
        formData.append('specification', itemData.specification);
        formData.append('material_unit', itemData.material_unit?.value || itemData.material_unit);

        if (itemData.boq_type === "Materials") {
            formData.append('quantity', itemData.quantity || "");
        } else if (itemData.boq_type === "Activities") {
            formData.append('days', itemData.days || "");
        }

        if (itemData.room_id) {
            formData.append('room_id', itemData.room_id);
        }

        if (itemData.inclusion_name) {
            formData.append('inclusion_name', itemData.inclusion_name);
        }

        if (itemData.image) {
            formData.append('budget_image', itemData.image);
        }

        // Lead Time — pass through when the modal/inline form supplied
        // a numeric value. AddItemModal already coerces to int|null based
        // on the supply_apply rule; if it's nullish or empty, we skip
        // the field entirely so the backend stores nothing.
        if (
            itemData.lead_time != null &&
            String(itemData.lead_time).trim() !== ""
        ) {
            formData.append('lead_time', String(itemData.lead_time).trim());
        }

        const response = await postFormData(
            BUDGET_ENDPOINTS.ADD_BOQ_TO_INCLUSION(projectId, inclusionId),
            formData
        );
        return response;
    } catch (error) {
        console.error("Error adding BOQ to inclusion:", error);
        throw error;
    }
};

export const deleteBOQ = async (projectId, boqId) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.DELETE_BOQ(projectId, boqId),
            "DELETE",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error deleting BOQ:", error);
        throw error;
    }
};

export const updateBOQ = async (projectId, boqId, itemData) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.UPDATE_BOQ(projectId, boqId),
            "PUT",
            itemData
        );
        return response;
    } catch (error) {
        console.error("Error updating BOQ:", error);
        throw error;
    }
};

export const deleteInclusion = async (inclusionId) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.DELETE_INCLUSION(inclusionId),
            "DELETE",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error deleting inclusion:", error);
        throw error;
    }
};

export const createRoomsConfiguration = async (projectId, body = {}) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.CREATE_ROOMS_CONFIGURATION(projectId),
            "POST",
            body
        );
        return response;
    } catch (error) {
        console.error("Error creating rooms configuration:", error);
        throw error;
    }
};

export const getRoomActivities = async (roomId) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.GET_ROOM_ACTIVITIES(roomId),
            "GET",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error fetching room activities:", error);
        throw error;
    }
};

export const getRoomInclusions = async (projectId, roomId) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.GET_ROOM_INCLUSIONS(projectId, roomId),
            "GET",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error fetching room inclusions:", error);
        throw error;
    }
};

export const getAvailableInclusionsForRoom = async (projectType, roomName) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.GET_AVAILABLE_INCLUSIONS_FOR_ROOM(projectType, roomName),
            "GET",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error fetching available inclusions for room:", error);
        throw error;
    }
};

export const getSubcategories = async (category, projectType, roomName = null) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.GET_SUBCATEGORIES(category, projectType, roomName),
            "GET",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error fetching subcategories:", error);
        throw error;
    }
};

export const addBOQToInclusionFormData = async (projectId, inclusionId, formData) => {
    try {
        const response = await postFormData(
            BUDGET_ENDPOINTS.ADD_BOQ_TO_INCLUSION(projectId, inclusionId),
            formData
        );
        return response;
    } catch (error) {
        console.error("Error adding BOQ to inclusion:", error);
        throw error;
    }
};

export const uploadBOQImage = async (boqId, file) => {
    try {
        const formData = new FormData();
        formData.append("project_id", boqId);
        formData.append("type", "budgetItemDetails");
        formData.append("file_1", file);
        const response = await postFormData(BUDGET_ENDPOINTS.UPLOAD_FILE, formData);
        return response;
    } catch (error) {
        console.error("Error uploading BOQ image:", error);
        throw error;
    }
};

export const deleteFile = async (fileId) => {
    try {
        const response = await getApiData(
            BUDGET_ENDPOINTS.DELETE_FILE(fileId),
            "POST",
            {}
        );
        return response;
    } catch (error) {
        console.error("Error deleting file:", error);
        throw error;
    }
};
